/**
 * Member Field Groups API client.
 *
 * Persists/loads a member's repeatable-group data (the array of instances they
 * have for each field_group) — separate from form_instances field_data, which
 * is form-scoped.
 */

import axios, { AxiosInstance } from 'axios';
import { API_VERSION } from './organizations';
import { initializeActiveCboId } from '../../utils/auth';

import { config as productConfig } from '@/src/config';
export interface MemberGroupInstance {
  instance_index: number;
  values: Record<string, any>;
}

export interface MemberFieldGroup {
  member_id: number;
  field_group_id: number;
  field_group_name: string;
  field_group_friendly_name: string;
  is_repeatable: boolean;
  instances: MemberGroupInstance[];
}

export interface MemberFieldGroupsList {
  member_id: number;
  groups: MemberFieldGroup[];
}

class MemberFieldGroupsAPI {
  private client: AxiosInstance;

  constructor(baseURL: string = (productConfig.weaveinApi.baseUrl ?? '').trim()) {
    initializeActiveCboId();
    this.client = axios.create({
      baseURL,
      headers: { 'Content-Type': 'application/json', 'X-API-Version': API_VERSION },
    });
    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem('auth_token');
      const cboId = localStorage.getItem('active_cbo_id');
      if (token) config.headers.Authorization = `Bearer ${token}`;
      if (cboId) config.headers['X-CBO-ID'] = cboId;
      return config;
    });
  }

  async list(memberId: number): Promise<MemberFieldGroupsList> {
    const r = await this.client.get<MemberFieldGroupsList>(
      `/api/members/${memberId}/field-groups`
    );
    return r.data;
  }

  async get(memberId: number, groupId: number): Promise<MemberFieldGroup> {
    const r = await this.client.get<MemberFieldGroup>(
      `/api/members/${memberId}/field-groups/${groupId}`
    );
    return r.data;
  }

  async setInstances(
    memberId: number,
    groupId: number,
    instances: Record<string, any>[]
  ): Promise<MemberFieldGroup> {
    const r = await this.client.put<MemberFieldGroup>(
      `/api/members/${memberId}/field-groups/${groupId}`,
      { instances }
    );
    return r.data;
  }

  async clear(memberId: number, groupId: number): Promise<void> {
    await this.client.delete(`/api/members/${memberId}/field-groups/${groupId}`);
  }
}

export const memberFieldGroupsAPI = new MemberFieldGroupsAPI();
